import {
  buildComputedRawSyncEventsBatchFromProjectionResult,
  buildCursorOnlyComputedRawSyncEventsBatch,
  buildNoChangeComputedRawSyncEventsBatch,
  computeRawSyncEventsBatch,
  type AgentRuntimeComputationState,
  type ComputedRawSyncEventsBatch,
} from "@/lib/agent-runtime-sync-batch-computation"
import type {
  ThreadSyncReplayWorkerEvent,
  ThreadSyncReplayWorkerRequest,
  ThreadSyncReplayWorkerResponse,
} from "@/lib/thread-sync-replay-worker-types"

type PendingReplayWorkerRequest = {
  currentState: AgentRuntimeComputationState
  startedAtMs: number
  resolve: (result: ThreadSyncReplayWorkerResolvedSuccessResult) => void
  reject: (error: Error) => void
}

export type ThreadSyncReplayWorkerResolvedSuccessResult = {
  computed: ComputedRawSyncEventsBatch
  estimatedProjectionBytes: number | null
  projectionSizeEstimateWasTruncated: boolean
}

export type ThreadSyncReplayWorkerComputeResult = {
  computed: ComputedRawSyncEventsBatch
  replaySessionId: string | null
  estimatedProjectionBytes: number | null
  projectionSizeEstimateWasTruncated: boolean
}

/**
 * Allow a couple of transient runtime worker failures before latching the worker
 * off for the session. If a deterministic worker bug is present, this prevents
 * recreating and crashing one worker per replay chunk forever while still giving
 * short-lived startup/import hiccups a chance to recover.
 */
const MAX_RUNTIME_REPLAY_WORKER_FAILURES = 3

/**
 * A replay-worker request should settle well before two seconds for the chunk
 * sizes we use today. If a worker hangs longer than that, tear it down and fall
 * back to main-thread replay instead of wedging catchup behind a never-resolving
 * worker promise.
 */
const REPLAY_WORKER_REQUEST_TIMEOUT_MS = 2_000

let replayWorker: Worker | null = null
let replayWorkerFailed = false
let runtimeReplayWorkerFailureCount = 0
let nextReplayWorkerRequestId = 0
let nextReplayWorkerSessionId = 0
const pendingReplayWorkerRequests = new Map<string, PendingReplayWorkerRequest>()
let replayWorkerTimeoutHandle: ReturnType<typeof setTimeout> | null = null

function clearReplayWorkerTimeoutHandle(): void {
  if (replayWorkerTimeoutHandle !== null) {
    clearTimeout(replayWorkerTimeoutHandle)
    replayWorkerTimeoutHandle = null
  }
}

function scheduleReplayWorkerTimeoutSweep(worker: Worker): void {
  clearReplayWorkerTimeoutHandle()
  if (pendingReplayWorkerRequests.size === 0) {
    return
  }

  const nowMs = Date.now()
  let nextDelayMs = REPLAY_WORKER_REQUEST_TIMEOUT_MS
  for (const pending of pendingReplayWorkerRequests.values()) {
    const remainingMs = pending.startedAtMs + REPLAY_WORKER_REQUEST_TIMEOUT_MS - nowMs
    nextDelayMs = Math.min(nextDelayMs, Math.max(0, remainingMs))
  }

  replayWorkerTimeoutHandle = setTimeout(() => {
    replayWorkerTimeoutHandle = null
    if (pendingReplayWorkerRequests.size === 0) {
      return
    }

    const timedOutAtMs = Date.now()
    for (const pending of pendingReplayWorkerRequests.values()) {
      if (timedOutAtMs - pending.startedAtMs >= REPLAY_WORKER_REQUEST_TIMEOUT_MS) {
        recordRuntimeReplayWorkerFailure(worker)
        rejectAllPendingReplayWorkerRequests(new Error("thread sync replay worker timed out"))
        return
      }
    }

    scheduleReplayWorkerTimeoutSweep(worker)
  }, nextDelayMs)
}

function recordRuntimeReplayWorkerFailure(worker: Worker): void {
  clearReplayWorkerTimeoutHandle()
  runtimeReplayWorkerFailureCount += 1
  if (runtimeReplayWorkerFailureCount >= MAX_RUNTIME_REPLAY_WORKER_FAILURES) {
    replayWorkerFailed = true
  }
  try {
    worker.terminate()
  } catch {
    // Ignore teardown failures; we still want subsequent calls to recreate a worker.
  }
  replayWorker = null
}

function rejectAllPendingReplayWorkerRequests(error: Error): void {
  clearReplayWorkerTimeoutHandle()
  const pendingRequests = Array.from(pendingReplayWorkerRequests.values())
  pendingReplayWorkerRequests.clear()
  for (const pending of pendingRequests) {
    pending.reject(error)
  }
}

export function resetThreadSyncReplayWorkerClientForTests(): void {
  rejectAllPendingReplayWorkerRequests(new Error("thread sync replay worker reset during test teardown"))

  try {
    replayWorker?.terminate()
  } catch {
    // Ignore teardown failures in tests.
  }
  replayWorker = null
  replayWorkerFailed = false
  runtimeReplayWorkerFailureCount = 0
  nextReplayWorkerRequestId = 0
  nextReplayWorkerSessionId = 0
}

function buildReplayWorkerRequestId(): string {
  nextReplayWorkerRequestId += 1
  return `thread-sync-replay-${nextReplayWorkerRequestId}`
}

function buildReplayWorkerSessionId(): string {
  nextReplayWorkerSessionId += 1
  return `thread-sync-replay-session-${nextReplayWorkerSessionId}`
}

function supportsReplayWorker(): boolean {
  return typeof Worker !== "undefined"
}

function normalizeReplayWorkerMaxEventId(value: unknown): number | null {
  if (value === null) {
    return null
  }

  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null
  }

  return Math.floor(value)
}

function normalizeReplayWorkerEstimatedBytes(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null
  }

  return Math.floor(value)
}

function resolveReplayWorkerSuccessResult(args: {
  currentState: AgentRuntimeComputationState
  response: Extract<ThreadSyncReplayWorkerResponse, { ok: true }>['result']
}): ThreadSyncReplayWorkerResolvedSuccessResult {
  if (args.response.type === "projection_batch") {
    const estimatedProjectionBytes = normalizeReplayWorkerEstimatedBytes(
      args.response.estimatedProjectionBytes,
    )
    if (estimatedProjectionBytes === null || typeof args.response.projectionSizeEstimateWasTruncated !== "boolean") {
      throw new Error("invalid projection batch replay-worker response")
    }

    return {
      computed: buildComputedRawSyncEventsBatchFromProjectionResult({
        currentState: args.currentState,
        result: args.response,
      }),
      estimatedProjectionBytes,
      projectionSizeEstimateWasTruncated: args.response.projectionSizeEstimateWasTruncated,
    }
  }

  if (args.response.type === "computed_batch") {
    return {
      computed: args.response.computed,
      estimatedProjectionBytes: null,
      projectionSizeEstimateWasTruncated: false,
    }
  }

  if (args.response.type === "cursor_only_progress") {
    const maxEventId = normalizeReplayWorkerMaxEventId(args.response.maxEventId)
    if (maxEventId === null) {
      throw new Error("invalid cursor-only replay-worker response")
    }

    return {
      computed: buildCursorOnlyComputedRawSyncEventsBatch({
        currentState: args.currentState,
        maxEventId,
      }),
      estimatedProjectionBytes: null,
      projectionSizeEstimateWasTruncated: false,
    }
  }

  if (args.response.type === "no_change") {
    const maxEventId =
      args.response.maxEventId === null
        ? null
        : normalizeReplayWorkerMaxEventId(args.response.maxEventId)
    if (args.response.maxEventId !== null && maxEventId === null) {
      throw new Error("invalid no-change replay-worker response")
    }

    return {
      computed: buildNoChangeComputedRawSyncEventsBatch({
        currentState: args.currentState,
        maxEventId,
      }),
      estimatedProjectionBytes: null,
      projectionSizeEstimateWasTruncated: false,
    }
  }

  throw new Error("unknown replay-worker success response type")
}

function attachReplayWorkerListeners(worker: Worker): void {
  worker.onmessage = (event: MessageEvent<ThreadSyncReplayWorkerResponse>) => {
    const response = event.data
    const pending = pendingReplayWorkerRequests.get(response.requestId)
    if (!pending) {
      return
    }

    if (response.ok) {
      try {
        const result = resolveReplayWorkerSuccessResult({
          currentState: pending.currentState,
          response: response.result,
        })
        pendingReplayWorkerRequests.delete(response.requestId)
        scheduleReplayWorkerTimeoutSweep(worker)
        runtimeReplayWorkerFailureCount = 0
        pending.resolve(result)
      } catch (error) {
        pendingReplayWorkerRequests.delete(response.requestId)
        scheduleReplayWorkerTimeoutSweep(worker)
        recordRuntimeReplayWorkerFailure(worker)
        const normalizedError =
          error instanceof Error ? error : new Error("thread sync replay worker response decode failed")
        pending.reject(normalizedError)
        rejectAllPendingReplayWorkerRequests(normalizedError)
      }
      return
    }

    pendingReplayWorkerRequests.delete(response.requestId)
    scheduleReplayWorkerTimeoutSweep(worker)
    recordRuntimeReplayWorkerFailure(worker)
    pending.reject(new Error(response.error))
    rejectAllPendingReplayWorkerRequests(new Error(response.error))
  }

  worker.onerror = () => {
    recordRuntimeReplayWorkerFailure(worker)
    rejectAllPendingReplayWorkerRequests(new Error("thread sync replay worker errored"))
  }
}

function getReplayWorker(): Worker | null {
  if (replayWorkerFailed || !supportsReplayWorker()) {
    return null
  }
  if (replayWorker) {
    return replayWorker
  }

  try {
    replayWorker = new Worker(new URL("../workers/thread-sync-replay.worker.ts", import.meta.url), {
      type: "module",
    })
    attachReplayWorkerListeners(replayWorker)
    return replayWorker
  } catch {
    replayWorkerFailed = true
    replayWorker = null
    return null
  }
}

function sendReplayWorkerDisposeRequest(args: {
  replaySessionId: string
}): void {
  const worker = replayWorker
  if (!worker || replayWorkerFailed) {
    return
  }

  try {
    worker.postMessage({
      type: "dispose_compute_raw_sync_batch_session",
      replaySessionId: args.replaySessionId,
    } satisfies ThreadSyncReplayWorkerRequest)
  } catch {
    // Best effort only; the next worker teardown also drops any in-worker session state.
  }
}

function postReplayWorkerComputeRequest(args: {
  worker: Worker
  currentState: AgentRuntimeComputationState
  request: Extract<ThreadSyncReplayWorkerRequest, { requestId: string }>
}): Promise<ThreadSyncReplayWorkerResolvedSuccessResult> {
  const { worker, currentState, request } = args

  return new Promise<ThreadSyncReplayWorkerResolvedSuccessResult>((resolve, reject) => {
    pendingReplayWorkerRequests.set(request.requestId, {
      currentState,
      startedAtMs: Date.now(),
      resolve: (result) => {
        resolve(result)
      },
      reject: (error) => {
        reject(error)
      },
    })
    scheduleReplayWorkerTimeoutSweep(worker)
    try {
      worker.postMessage(request)
    } catch (error) {
      const pending = pendingReplayWorkerRequests.get(request.requestId)
      pendingReplayWorkerRequests.delete(request.requestId)
      scheduleReplayWorkerTimeoutSweep(worker)
      recordRuntimeReplayWorkerFailure(worker)
      pending?.reject(
        error instanceof Error ? error : new Error("thread sync replay worker postMessage failed"),
      )
    }
  })
}

export function disposeThreadSyncReplayWorkerSession(args: {
  replaySessionId: string | null
}): void {
  if (!args.replaySessionId) {
    return
  }

  sendReplayWorkerDisposeRequest({
    replaySessionId: args.replaySessionId,
  })
}

export async function computeRawSyncEventsBatchOffMainThread(args: {
  currentState: AgentRuntimeComputationState
  events: ThreadSyncReplayWorkerEvent[]
  replaySessionId?: string | null
}): Promise<ThreadSyncReplayWorkerComputeResult> {
  const worker = getReplayWorker()
  if (!worker) {
    return {
      computed: computeRawSyncEventsBatch({
        currentState: args.currentState,
        events: args.events,
      }),
      replaySessionId: null,
      estimatedProjectionBytes: null,
      projectionSizeEstimateWasTruncated: false,
    }
  }

  const previousReplaySessionId =
    typeof args.replaySessionId === "string" && args.replaySessionId.length > 0
      ? args.replaySessionId
      : null
  const nextReplaySessionId = previousReplaySessionId ?? buildReplayWorkerSessionId()
  const requestId = buildReplayWorkerRequestId()
  const request: Extract<ThreadSyncReplayWorkerRequest, { requestId: string }> = previousReplaySessionId
    ? {
        requestId,
        type: "continue_compute_raw_sync_batch_session",
        replaySessionId: previousReplaySessionId,
        events: args.events,
      }
    : {
        requestId,
        type: "start_compute_raw_sync_batch_session",
        replaySessionId: nextReplaySessionId,
        currentState: args.currentState,
        events: args.events,
      }

  try {
    const computed = await postReplayWorkerComputeRequest({
      worker,
      currentState: args.currentState,
      request,
    })
    return {
      computed: computed.computed,
      replaySessionId: nextReplaySessionId,
      estimatedProjectionBytes: computed.estimatedProjectionBytes,
      projectionSizeEstimateWasTruncated: computed.projectionSizeEstimateWasTruncated,
    }
  } catch {
    if (previousReplaySessionId !== null) {
      sendReplayWorkerDisposeRequest({
        replaySessionId: previousReplaySessionId,
      })
    }

    return {
      computed: computeRawSyncEventsBatch({
        currentState: args.currentState,
        events: args.events,
      }),
      replaySessionId: null,
      estimatedProjectionBytes: null,
      projectionSizeEstimateWasTruncated: false,
    }
  } finally {
    if (pendingReplayWorkerRequests.delete(requestId)) {
      scheduleReplayWorkerTimeoutSweep(worker)
    }
  }
}
